import type { ArcDrawMode } from '@/plugins/cad/module/CADModule/draw/drawArcTool'
import type { CircleDrawMode } from '@/plugins/cad/module/CADModule/draw/drawCircleTool'
import type { RectDrawMode } from '@/plugins/cad/module/CADModule/draw/drawRectTool'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import type { Controller } from '@/plugins/cad/module/core/controller/Controller'

/**
 * CAD 绘制工具类型枚举，覆盖图形绘制与图形标注两类。
 */
export enum DrawToolType {
  /** 多段线绘制 */
  Polyline = 'Polyline',
  /** 圆弧绘制 */
  Arc = 'Arc',
  /** 样条曲线绘制 */
  Spline = 'Spline',
  /** 圆绘制 */
  Circle = 'Circle',
  /** 矩形绘制 */
  Rect = 'Rect',
  /** 多边形绘制 */
  Polygon = 'Polygon',
  /** 椭圆绘制 */
  Ellipse = 'Ellipse',
  /** 射线绘制 */
  Ray = 'Ray',
  /** 填充绘制 */
  Hatch = 'Hatch',

  /** 多行文字标注 */
  Text = 'Text',
  /** 长度标注 */
  Dimension = 'Dimension',
  /** 多重引线 */
  MLeader = 'MLeader',
  /** 角度标注 */
  AngularDimension = 'AngularDimension',
}

/**
 * CAD 编辑工具类型枚举。
 */
export enum EditToolType {
  /** 合并相邻线段 */
  Join = 'Join',
  /** 复制实体 */
  Copy = 'Copy',
  /** 移动实体 */
  Move = 'Move',
  /** 闭合多段线 */
  Close = 'Close',
  /** 旋转实体 */
  Rotate = 'Rotate',
  /** 偏移实体 */
  Offset = 'Offset',
  /** 打断实体 */
  Break = 'Break',
  /** 修剪实体 */
  Trim = 'Trim',
  /** 延伸实体 */
  Extend = 'Extend',
}

// 基础绘制工具参数接口
interface IBaseDrawToolParams {
  entityData?: Partial<IEntity> // 可选(用于初始化图元数据)
  controller?: Controller // 可选(不传则使用CADController)
}

// 需要模式的工具参数
interface IDrawToolWithMode<T> extends IBaseDrawToolParams {
  mode: T
}

// 圆弧绘制工具参数接口
interface IArcDrawToolParams extends IDrawToolWithMode<ArcDrawMode> {
  toolType: DrawToolType.Arc
}

// 圆绘制工具参数接口
interface ICircleDrawToolParams extends IDrawToolWithMode<CircleDrawMode> {
  toolType: DrawToolType.Circle
}

// 矩形绘制工具参数接口
interface IRectDrawToolParams extends IDrawToolWithMode<RectDrawMode> {
  toolType: DrawToolType.Rect
}

// 普通绘制工具参数接口
interface INormalDrawToolParams extends IBaseDrawToolParams {
  toolType: Exclude<DrawToolType, DrawToolType.Arc | DrawToolType.Circle | DrawToolType.Rect>
}

/** 绘制工具参数联合类型，按工具类型区分不同的 mode 字段 */
export type IDrawToolParams =
  | INormalDrawToolParams
  | IArcDrawToolParams
  | ICircleDrawToolParams
  | IRectDrawToolParams

/**
 * 编辑工具参数。
 */
export interface IEditToolParams {
  /** 编辑工具类型 */
  toolType: EditToolType
  /** 承载该工具的控制器，缺省时使用默认 CADController */
  controller?: Controller
}
